"use client";

import React from "react";
import { Star, Quote } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
const testimonials = [{
  id: 1,
  name: "Julian Sterling",
  date: "Managing Director, Global Logistics",
  quote: "Sterling Law Associates provided the architectural precision our firm required for complex real estate acquisitions. Their counsel is as stable as the steel and glass structures they protect.",
  avatarSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687512/img/client_julian_sterling.png",
  avatarSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687512/img/client_julian_sterling.png-wvc-srcset",
  avatarSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687512/img/client_julian_sterling.png-wvc-sizes"
}, {
  id: 2,
  name: "Eleanor Vance",
  date: "Principal, Vance & Co.",
  quote: "In the realm of family law, Sterling Law combines traditional authority with modern empathy. They navigated our sensitive estate matters with unshakeable stability and elite intelligence.",
  avatarSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687512/img/client_eleanor_vance.png",
  avatarSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687512/img/client_eleanor_vance.png-wvc-srcset",
  avatarSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687512/img/client_eleanor_vance.png-wvc-sizes"
}, {
  id: 3,
  name: "Marcus Thorne",
  date: "CEO, Thorne Enterprises",
  quote: "The transparency and professional clarity offered by Sterling Law is unmatched. Their formal approach and deep legal insights have become a cornerstone of our corporate strategy.",
  avatarSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687512/img/client_marcus_thorne.png",
  avatarSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687512/img/client_marcus_thorne.png-wvc-srcset",
  avatarSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687512/img/client_marcus_thorne.png-wvc-sizes"
}, {
  id: 4,
  name: "Sophia Chen",
  date: "Founder, Heritage Group",
  quote: "Sterling Law Associates represents the gold standard of legal prestige. Their attention to detail in our commercial contracts was nothing short of architectural perfection.",
  avatarSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687512/img/client_sophia_chen.png",
  avatarSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687512/img/client_sophia_chen.png-wvc-srcset",
  avatarSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687512/img/client_sophia_chen.png-wvc-sizes"
}, {
  id: 5,
  name: "Arthur Blackwell",
  date: "Senior Partner, Blackwell Estates",
  quote: "A firm that commands attention through results. Their deep burgundy precision and charcoal-grey stability make them the only choice for high-stakes litigation in the city.",
  avatarSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687512/img/client_arthur_blackwell.png",
  avatarSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687512/img/client_arthur_blackwell.png-wvc-srcset",
  avatarSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1687512/img/client_arthur_blackwell.png-wvc-sizes"
}];
export default function ClientPerspectives() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: carouselRef,
    isVisible: isCarouselVisible
  } = useRevealOnScroll({
    threshold: 0.1,
    once: true
  });
  return <section data-section-id="259653" id="client-perspectives" className="bg-background text-foreground py-16 md:py-24 overflow-hidden">
      <div className="container mx-auto px-4 md:px-6">
        <div ref={headerRef} className={`flex flex-col items-center text-center mb-12 transition-all duration-700 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-default font-bold text-foreground mb-4 tracking-tight">
            Client <span className="text-primary">Perspectives</span>
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl font-default leading-relaxed">
            The Sterling Standard is best articulated by those we represent. Discover the experiences of our distinguished clientele.
          </p>
        </div>

        <div ref={carouselRef} className={`transition-all duration-1000 delay-200 ease-out ${isCarouselVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
          <Carousel opts={{
          align: "start",
          loop: true
        }} className="w-full max-w-6xl mx-auto">
            <CarouselContent className="-ml-4 md:-ml-6">
              {testimonials.map((testimonial, i) => <CarouselItem key={testimonial.id} data-index={i} className="pl-4 md:pl-6 md:basis-1/2 lg:basis-1/3">
                  <Card className="h-full flex flex-col border-border shadow-lg hover:shadow-xl transition-all duration-500 bg-card rounded-md">
                    <CardHeader className="pb-4 flex flex-row items-start gap-4">
                      <div className="shrink-0">
                        <img src={testimonial.avatarSrc} data-wvc-srcset={testimonial.avatarSrcset} data-wvc-sizes={testimonial.avatarSizes} alt={`${testimonial.name} avatar`} className="w-12 h-12 object-cover rounded-md border border-secondary/30 grayscale hover:grayscale-0 transition-all duration-500" loading="lazy" />
                      </div>
                      <div className="flex flex-col flex-1 border-l border-primary pl-4">
                        <h3 className="font-default font-bold text-foreground text-lg leading-tight tracking-wide uppercase">
                          {testimonial.name}
                        </h3>
                        <span className="text-xs text-muted-foreground font-mono mt-1 uppercase tracking-widest">
                          {testimonial.date}
                        </span>
                        <div className="flex items-center mt-2 gap-0.5">
                          {[...Array(5)].map((_, starIndex) => <Star key={starIndex} className="w-3 h-3 fill-primary text-primary" />)}
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="flex-1 pt-0">
                      <Quote className="w-8 h-8 text-primary/10 mb-2" />
                      <p className="text-card-foreground font-serif italic text-lg leading-relaxed">
                        "{testimonial.quote}"
                      </p>
                    </CardContent>
                  </Card>
                </CarouselItem>)}
            </CarouselContent>
            <div className="hidden md:block">
              <CarouselPrevious className="bg-background border-secondary/20 text-secondary hover:bg-primary hover:text-primary-foreground rounded-md transition-colors" />
              <CarouselNext className="bg-background border-secondary/20 text-secondary hover:bg-primary hover:text-primary-foreground rounded-md transition-colors" />
            </div>
          </Carousel>
        </div>
      </div>
    </section>;
}